/*
 * Copyright 2025 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { Box, TextField, Typography, useTheme } from "@material-ui/core";
import CheckIcon from "@material-ui/icons/Check";
import Autocomplete from "@material-ui/lab/Autocomplete";
import React, { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CopyIconButton } from "../copy-icon-button/copy-icon-button.component";
import { WorkspaceData } from "../rest/workspace.rest";
import { useWorkspaceContext } from "../workspace-provider/workspace-provider.component";
import { AutocompleteVirtualisedListbox } from "./autocomplete-virtualised-listbox.component";
import { useWorkspaceSwitcherSelectStyles } from "./workspace-switcher.styles";

export const WorkspaceSwitcher: FunctionComponent = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { selectedWorkspace, workspaceList, updateWorkspace } =
        useWorkspaceContext();
    const className = useWorkspaceSwitcherSelectStyles();

    const handleChangeWorkspace = (
        _: unknown,
        newWorkspace: WorkspaceData | null
    ): void => {
        if (
            !newWorkspace?.workspaceId ||
            newWorkspace.workspaceId === selectedWorkspace?.workspaceId
        ) {
            return;
        }

        updateWorkspace(newWorkspace);

        setTimeout(() => {
            window.location.reload();
        }, 150);
    };

    // show the selected workspace first in the list
    const orderedWorkspaceList = useMemo(() => {
        if (!selectedWorkspace?.workspaceId) {
            return workspaceList;
        }

        const selected = workspaceList.find(
            (w) => w.workspaceId === selectedWorkspace.workspaceId
        );
        const others = workspaceList.filter(
            (w) => w.workspaceId !== selectedWorkspace.workspaceId
        );

        return selected ? [selected, ...others] : workspaceList;
    }, [workspaceList, selectedWorkspace?.workspaceId]);

    const isWorkspaceSwitcherVisible = useMemo(() => {
        if (workspaceList.length === 0) {
            return false;
        }
        if (
            workspaceList.length === 1 &&
            workspaceList[0].workspaceId === "default"
        ) {
            return false;
        }

        return true;
    }, [workspaceList]);

    if (!isWorkspaceSwitcherVisible) {
        return null;
    }

    return (
        <Autocomplete
            disableClearable
            disableListWrap
            ListboxComponent={AutocompleteVirtualisedListbox}
            className={className.input}
            filterOptions={(options, { inputValue }) => {
                const lowerCaseInput = inputValue.toLowerCase();

                return options.filter(
                    (workspace) =>
                        workspace.workspaceName
                            .toLowerCase()
                            .includes(lowerCaseInput) ||
                        workspace.workspaceId
                            .toLowerCase()
                            .includes(lowerCaseInput) ||
                        workspace.workspaceId === selectedWorkspace.workspaceId
                );
            }}
            getOptionLabel={(option) => option.workspaceName}
            getOptionSelected={(option, value) => {
                return option.workspaceId === value.workspaceId;
            }}
            options={orderedWorkspaceList}
            renderInput={(params) => (
                <TextField
                    {...params}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                            <Typography
                                color="textSecondary"
                                style={{ marginLeft: 8 }}
                            >
                                {t("label.workspace")}:
                            </Typography>
                        ),
                    }}
                    placeholder={t("label.search-workspace")}
                    variant="outlined"
                />
            )}
            renderOption={(option) => (
                <Box
                    alignItems="center"
                    display="flex"
                    justifyContent="space-between"
                    key={option.workspaceId}
                    width="100%"
                >
                    <Box alignItems="center" display="flex">
                        {selectedWorkspace.workspaceId ===
                        option.workspaceId ? (
                            <CheckIcon
                                fontSize="small"
                                style={{
                                    color: theme.palette.success.main,
                                    marginRight: 8,
                                }}
                            />
                        ) : (
                            <Box width={28} />
                        )}
                        <Box>
                            <Typography variant="body1">
                                {option.workspaceName || option.workspaceId}
                            </Typography>
                            <Typography color="textSecondary" variant="caption">
                                {option.workspaceId}
                            </Typography>
                        </Box>
                    </Box>
                    <CopyIconButton
                        text={option.workspaceId}
                        tooltip={t("label.copy-workspace-id")}
                    />
                </Box>
            )}
            size="small"
            style={{ width: 300 }}
            value={selectedWorkspace}
            onChange={handleChangeWorkspace}
        />
    );
};
